<template>
  <div>
    <v-app-bar flat color="transparent" class="main-appbar">
      <app-bar-nav-icon />

      <v-skeleton-loader
        type="chip"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>
      <v-toolbar-title v-else class="headline font-weight-bold"
        >Reports ({{ listMeta.total }})</v-toolbar-title
      >

      <v-spacer></v-spacer>
      <v-row v-show="showSearch" align="center" style="max-width: 300px">
        <v-text-field
          class="search"
          ref="searchField"
          flat
          hide-details
          solo
          rounded
          clearable
          background-color="grey lighten-3"
          label="Search post content or reporter name"
          v-model="filter.search"
          @input="search"
        ></v-text-field>
      </v-row>

      <v-skeleton-loader
        type="actions"
        transition="fade-transition"
        v-if="loading"
      ></v-skeleton-loader>

      <template v-else>
        <v-btn icon class="ml-5" @click="showSearchField">
          <img src="@/assets/icons/search.svg" alt height="17.7px" />
        </v-btn>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              color="#383d3f"
              class="mr-1"
              icon
              @click="exportList"
              v-on="on"
            >
              <v-icon>{{ icons.export }}</v-icon>
            </v-btn>
          </template>
          <span>Export to Excel</span>
        </v-tooltip>
      </template>
    </v-app-bar>

    <div class="d-flex mt-6">
      <v-select
        hide-details
        flat
        solo
        dense
        label="All Reports"
        clearable
        item-text="label"
        item-value="value"
        v-model="filter.type"
        :items="[
          { value: 'only_mentors', label: 'Mentor reports' },
          { value: 'only_resolved', label: 'Resolved reports' },
          { value: 'only_unresolved', label: 'Unresolved reports' },
        ]"
        style="max-width: 180px"
        @change="filterReport"
      />

      <v-select
        class="ml-4"
        hide-details
        flat
        solo
        dense
        label="Type"
        clearable
        item-text="label"
        item-value="value"
        v-model="filter.model"
        :items="[
          { value: 'only_posts', label: 'Posts' },
          { value: 'only_comments', label: 'Comments' },
        ]"
        style="max-width: 180px"
        @change="filterReport"
      />

      <v-select
        class="ml-4"
        hide-details
        flat
        solo
        dense
        label="Tag"
        clearable
        :full-width="false"
        item-text="tag_name"
        item-value="id"
        v-model="filter.tag"
        :items="tags"
        style="max-width: 180px"
        @change="filterReport"
      />

      <v-select
        class="ml-4"
        hide-details
        flat
        solo
        dense
        label="Reason"
        clearable
        item-text="label"
        item-value="id"
        v-model="filter.reason"
        :items="reportCategories"
        style="max-width: 180px"
        @change="filterReport"
      />

      <v-spacer></v-spacer>

      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            class="ml-4"
            flat
            solo
            single-line
            clearable
            dense
            readonly
            v-on="on"
            v-model="reportDateFormatted"
            placeholder="Date reported"
            :append-icon="icons.calendar"
            @click:clear="clearDateFilter"
            style="max-width: 280px"
          ></v-text-field>
        </template>

        <v-date-picker
          v-model="filter.date_reported"
          no-title
          scrollable
          color="primary"
          :max="currentDate"
          @input="filterReport"
        ></v-date-picker>
      </v-menu>
    </div>

    <div
      class="mx-4 pb-10"
      id="report-list"
      v-infinite-scroll="loadMore"
      :infinite-scroll-disabled="loading"
      infinite-scroll-distance="100"
      min-width="200px"
    >
      <v-card
        v-for="report in reports"
        :key="report.id"
        max-width="620"
        class="mx-auto my-10 py-4 px-4"
      >
        <v-alert
          dense
          text
          type="success"
          v-if="
            get(report, 'reportable.deleted_at') !== null ||
            (get(report, 'reportable.deleted_at') === null &&
              report.is_denied === true)
          "
        >
          Resolution: Reported content has been
          {{ report.is_denied ? 'KEPT.' : 'DELETED' }}
        </v-alert>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="title text-wrap pr-5 hoverable">
              <!-- eslint-disable-next-line -->
              {{ report.reason ? report.reason.label : '' }}
            </v-list-item-title>
            <div class="pa-0 pt-1" label color="none" text-color="primary">
              <v-avatar class="mr-2" size="20">
                <img
                  :src="get(report, 'reporter.avatar_permanent_url')"
                  :alt="get(report, 'reporter.display_name')"
                />
              </v-avatar>

              <span class="hoverable body-2 primary--text">
                <!-- eslint-disable-next-line -->
                {{ get(report, 'reporter.display_name') }}
              </span>

              <span
                class="caption"
                v-if="get(report, 'reporter.role') === 'Mentor'"
              >
                <span class="mx-1 body-2 grey--text">•</span> Womn-Kind Mentor
              </span>

              <span class="mx-1 caption grey--text">•</span>
              <span class="caption grey--text">{{ report.addedDate }}</span>
            </div>
          </v-list-item-content>
        </v-list-item>

        <v-card-text>
          <div class="pb-5 px-12 pb-0">
            <v-card elevation="0" style="background: #e9e9ed">
              <post-card-content :report="report" />
            </v-card>
          </div>
          <div class="body-1 text--primary" v-if="report.description">
            Remarks: {{ report.description }}
          </div>
        </v-card-text>
      </v-card>

      <div
        class="d-flex justify-center mt-3"
        v-if="!(reports.length > 0) && !loading"
      >
        <p>No match found.</p>
      </div>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" right>
        {{ snackbar.message }}
        <v-btn color="white" text @click="snackbar.show = false">Ok</v-btn>
      </v-snackbar>

      <v-progress-linear
        indeterminate
        v-if="loadMoreIndicator"
        class="mx-auto my-10 load-more-indicator"
        color="grey"
      ></v-progress-linear>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import {
  mdiDotsVertical,
  mdiUnfoldMoreHorizontal,
  mdiMagnify,
  mdiAccountCircleOutline,
  mdiChevronRight,
  mdiFileExcel,
  mdiCalendar,
} from '@mdi/js'
import infiniteScroll from 'vue-infinite-scroll'
import debounce from 'lodash/debounce'
import ControlsMixin from '@/utils/mixins/Controls'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import startCase from 'lodash/startCase'
import PostCardContent from './PostCardContent.vue'
import get from 'lodash/get'
import dayjs from 'dayjs'

export default {
  name: 'ReportsPage',

  mixins: [ControlsMixin],

  directives: {
    infiniteScroll,
  },

  components: {
    AppBarNavIcon,
    PostCardContent,
  },

  data() {
    return {
      loadMoreIndicator: false,
      loading: false,
      showSearch: false,
      menu: false,
      reportDateFormatted: '',
      filter: {
        search: '',
        tag: '',
        reason: '',
        type: 'only_unresolved',
        model: '',
        date_reported: '',
      },
      icons: {
        settings: mdiDotsVertical,
        sort: mdiUnfoldMoreHorizontal,
        search: mdiMagnify,
        view: mdiAccountCircleOutline,
        arrow: mdiChevronRight,
        export: mdiFileExcel,
        calendar: mdiCalendar,
      },
      snackbar: {
        show: false,
        message: null,
        color: null,
      },
    }
  },

  created() {
    this.clearTags()
    this.fetchTags(1)
    this.clearReportCategories()
    this.fetchReportCategories(1)
  },

  computed: {
    ...mapState({
      reports: (state) => state.report.list,
      listMeta: (state) => state.report.listMeta,
      tags: (state) => state.tag.list,
      reportCategories: (state) => state.reportCategory.list,
    }),

    currentDate() {
      return dayjs(new Date()).format('YYYY-MM-DD')
    },

    paginationLength() {
      return (this.listMeta.total / this.listMeta.per_page) | 0
    },
  },

  methods: {
    get,
    ...mapActions({
      getReports: 'report/getReports',
      getTags: 'tag/getTags',
      getReportCategories: 'reportCategory/getReportCategories',
      exportToExcel: 'report/exportToExcel',
    }),

    startCase,
    ...mapMutations({
      clearTags: 'tag/clearTagList',
      clearReportCategories: 'reportCategory/clearReportCategoryList',
      clearReport: 'report/clearReportsList',
    }),

    pageChanged(page) {
      this.fetchReports(page)
    },

    async loadMore() {
      if (this.listMeta.current_page < this.listMeta.last_page) {
        try {
          this.loadMoreIndicator = true
          await this.fetchReports(this.listMeta.current_page + 1)
        } finally {
          this.loadMoreIndicator = false
        }
      }
    },

    search: debounce(function () {
      this.clearReport()
      this.fetchReports()
    }, 600),

    async fetchReports(page = 1) {
      let params = { page }
      if (this.filter.search) {
        params.search = this.filter.search
      }
      if (this.filter.tag) {
        params.tag = this.filter.tag
      }
      if (this.filter.reason) {
        params.reason = this.filter.reason
      }
      if (this.filter.type) {
        params.type = this.filter.type
      }
      if (this.filter.model) {
        params.model = this.filter.model
      }
      if (this.filter.date_reported) {
        params.date_reported = this.filter.date_reported
      }

      this.loading = true
      await this.getReports(params)
      this.loading = false
    },

    async showSearchField() {
      this.showSearch = !this.showSearch
      await this.$nextTick()
      this.$refs.searchField.$refs.input.focus()
    },

    async fetchReportCategories(page = 1) {
      this.loading = true
      await this.getReportCategories({ page })
      this.loading = false
    },

    async fetchTags(page = 1) {
      this.loading = true
      await this.getTags({ page })
      this.loading = false
    },

    clearDateFilter() {
      this.filter.date_reported = ''
      this.filterReport()
    },

    filterReport(filterValue) {
      if (this.filter.date_reported) {
        this.reportDateFormatted = `${dayjs(this.filter.date_reported).format(
          'MMMM DD, YYYY'
        )}`
      }
      this.clearReport()
      this.fetchReports(null, filterValue)
      this.menu = false
    },

    async exportList() {
      this.loading = true
      await this.exportToExcel({
        search: this.filter.search,
        type: this.filter.type,
        model: this.filter.model,
        tag: this.filter.tag,
        reason: this.filter.reason,
        date_reported: this.filter.date_reported,
      })
        .then((response) => {
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: 'application/vnd.ms-excel',
            })
          )
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'reports_list.xlsx')
          document.body.appendChild(link)
          link.click()
          this.showSnackbar('File successfully downloaded', 'success')
        })
        .catch(() => {
          this.showSnackbar('Ops! Something went wrong', 'red')
        })
      this.loading = false
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    },
  },
}
</script>
<style scoped>
.load-more-indicator {
  max-width: 620px;
}
div.v-menu__content.theme--light.menuable__content__active {
  margin-top: -20px;
}
</style>
